import React, { useEffect, useState } from 'react';
import EventCard from '../../components/modules/events/EventCard'
import { toast } from "sonner";
import { AnalyticIcon, PlusIcon, SettingsIcon } from '../../assets/icons/icons';
import IconButton from '../../components/shared/IconButton';
import { useNavigate } from 'react-router';
import CreateEventForm from '../../components/forms/CreateEventForm';
import { apiConnecter } from '../../services/apiConnector';
import { useSelector } from 'react-redux';
import Modal from '../../components/shared/ErrorModal';
import SearchBox from '../../components/shared/SearchBox';
import InfiniteScroll from 'react-infinite-scroll-component';
import API_ENDPOINTS from '../../constant/EndPoints';

const MAX_HEIGHT_CALC = 'max-h-[calc(100vh-240px)]';

const Events = () => {
    /* ****************************************************************
     *                            STATES                             *
     ******************************************************************/
    const userInfo = useSelector((state) => state.userInfo);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [allEvents, setAllEvents] = useState([]);
    const [eventToUpdate, setEventToUpdate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const navigate = useNavigate();
    const LIMIT = 8;

    /* ****************************************************************
    *                            SERVICES                             *
    ******************************************************************/

    /**
 * Fetches a list of all events with pagination. Optionally appends the events to the current list.
 * Updates the state with the fetched events and checks if there are more events to load.
 * 
 * @param {number} pageNum - The page number to fetch events from (defaults to 1).
 * @param {boolean} append - If true, appends the new events to the existing list (defaults to false).
 * @returns {void}
 */

    const getAllEventsHandler = async (pageNum = 1, append = false) => {
        setLoading(true);
        try {
            const response = await apiConnecter("GET", `${API_ENDPOINTS.REACT_APP_GET_ALL_EVENTS_END_POINT}?page=${pageNum}&limit=${LIMIT}`,
                null, { authorization: `Bearer ${userInfo.token}` });
            console.log("get all events api response", response);
            const newEvents = response.data.result;
            if (append) {
                setAllEvents(prev => [...prev, ...newEvents]);
                setFilteredEvents(prev => [...prev, ...newEvents]);
            } else {
                setAllEvents(newEvents);
                setFilteredEvents(newEvents);
            }
            setHasMore(newEvents.length === LIMIT);; // If we get full page (2 items), there might be more
        } catch (err) {
            setError({ title: "Error", message: err?.response?.data?.message ?? err.message });
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Deletes an event by its ID and fetches the updated list of events.
     * Displays a loading modal while the event is being deleted and shows a success/error message afterward.
     * 
     * @param {string} id - The ID of the event to delete.
     * @returns {void}
     */
    const deleteEvent = async (id) => {
        const toastId = Modal({
            title: "Deleting Event...",
            loading: true,
            loadingText: "Please wait while the Event is being deleted...",
        });

        try {
            await apiConnecter(
                "DELETE",
                `${API_ENDPOINTS.REACT_APP_DELETE_EVENT_BY_ID_END_POINT}/${id}`,
                null,
                { authorization: `Bearer ${userInfo.token}` }
            );



            toast.dismiss(toastId);
            Modal({
                "message": "Event deleted successfully", title: "Success"
            })

            await getAllEventsHandler();
        } catch (err) {
            toast.dismiss(toastId);
            // toast.error(err?.response?.data?.message ?? "Failed to delete organizer");
            Modal({ "message": err?.response?.data?.message ?? err.message, title: "Error" });
            console.error('Error deleting event:', err);
        }
    };


    /* ****************************************************************
    *                            HELPER FUNCTION                      *
    ******************************************************************/
    const loadMoreEvents = () => {
        setPage(prev => {
            const nextPage = prev + 1;
            getAllEventsHandler(nextPage, true);
            return nextPage;
        });
    };

    /* ****************************************************************
      *                             EFFECT HOOKS                        *
      ******************************************************************/
    useEffect(() => {
        getAllEventsHandler();
    }, []);

    useEffect(() => {
        if (error) {
            Modal({
                title: error.title || "Error",
                message: error.message,
            });
        }
        if (success) {
            Modal({
                title: success.title || "Success",
                message: success.message,
            });
        }

        setTimeout(() => {
            setSuccess('');
            setError('');
        }, 3000);
    }, [error, success]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredEvents(allEvents);
        } else {
            const lowerSearch = searchTerm.toLowerCase();

            const filtered = allEvents.filter(event => {
                // Organizer matching (for multiple organizers)
                const organizerMatch = event.organizers?.some(org =>
                    org.userName?.toLowerCase().includes(lowerSearch) ||
                    org.email?.toLowerCase().includes(lowerSearch)
                );

                // Field matching
                const matchEventName = event.eventName?.toLowerCase().includes(lowerSearch);
                const matchLocation = event.location?.toLowerCase().includes(lowerSearch);
                const matchStatus = event.status?.toLowerCase().includes(lowerSearch); // ✅ added status

                // Return true if any match
                return matchEventName || organizerMatch || matchLocation || matchStatus;
            });

            setFilteredEvents(filtered);
        }
    }, [searchTerm, allEvents]);

    return (
        <div className='flex flex-col w-full m-5 p-4 sm:p-0'>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className='pr-4 '>
                    <h2 className="text-2xl font-semibold text-[var(--primary-text-color)]">Events</h2>
                    <p className="text-[var(--primary-text-color)] text-sm mt-1">Manage SparkMotion events and bracelet configurations</p>
                </div>
                <IconButton
                    icon={PlusIcon}
                    bgColor='bg-[var(--analytics-card-color)]'
                    label="Create Event"
                    onClick={() => {
                        setShowForm(true);
                        setEventToUpdate(null);
                    }}
                    hoverColor="hover:bg-orange-600"

                />
            </div>
            {/* Event Card main Div */}
            <div id="scrollableDiv" className={`overflow-y-auto ${MAX_HEIGHT_CALC} custom-scrollbar pr-2`}>
                {loading && filteredEvents.length === 0 && (
                    <div className="text-center py-4 bg-[var(--color-surface-background)] rounded-md">
                        <div className="loader"></div>
                    </div>
                )}
                <InfiniteScroll
                    dataLength={filteredEvents?.length}
                    next={loadMoreEvents}
                    hasMore={hasMore}
                    loader={<div className="text-center py-4 bg-[var(--color-surface-background)] rounded-md">
                        <div className="loader"></div>
                    </div>}
                    scrollableTarget="scrollableDiv"
                >
                    {filteredEvents?.map((event, index) => (
                        <EventCard key={index} event={event} deleteEvent={deleteEvent} onEdit={() => {
                            setEventToUpdate(event);
                            setShowForm(true);

                        }} />
                    ))}
                    {filteredEvents?.length === 0 && !loading && (
                        <p className='text-[var(--color-text-secondary)] border border-[var(--border-color)] text-center p-5 bg-[var(--color-surface-background)] rounded-md'>
                            No events found. Please create an event.
                        </p>
                    )}
                </InfiniteScroll>
            </div>
            {/* Form to create event */}
            {showForm && <CreateEventForm setShowForm={setShowForm} eventToUpdate={eventToUpdate} onCancel={() => setShowForm(false)} setSuccess={setSuccess} setError={setError} setAllEvents={setAllEvents} getAllEventsHandler={getAllEventsHandler} />}
        </div>
    );
};

export default Events;